import React,{useEffect} from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import ProtectedRoute from './components/ProtectedRoute';
import MobileBottomNav from './components/MobileBottomNav';
import ErrorBoundary from './components/ErrorBoundary';

// Auth Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Public Pages
import LandingPage from './pages/LandingPage';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import AllCourses from './pages/AllCourses';
import CourseDetailsPage from './pages/CourseDetailsPage';
import PaymentCallback from './pages/PaymentCallback';
import BlogList from './pages/Blog/BlogList';
import BlogPost from './pages/Blog/BlogPost';
import PolicyPages from './pages/Blog/PolicyPages';
// Protected Pages
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import MyCourses from './pages/MyCourses';
import CourseContents from './pages/CourseContents';
import ContentViewer from './pages/ContentViewer';
import TestSeriesDetail from './pages/TestSeriesDetail';
import QuizSelection from './pages/QuizSelection';
import QuizPage from './pages/QuizPage';
import ResultPage from './pages/ResultPage';
import PrintResultPage from './pages/PrintResultPage';
import QuizHistory from './pages/QuizHistory';

// AI Subscription Pages
import AIFeaturesHub from './pages/AIFeaturesHub';
import SubscriptionPlans from './pages/SubscriptionPlans';
import MySubscription from './pages/MySubscription';
import AIQuizGenerator from './pages/AIQuizGenerator';
import AIQuizPage from './pages/AIQuizPage';
import AIDoubtSolver from './pages/AIDoubtSolver';
import AINoteSummarizer from './pages/AINoteSummarizer';
import AIFeatureComingSoon from './pages/AIFeatureComingSoon';
import AIFlashcardGenerator from './pages/AIFlashcardGenerator';
import AIStudyPlanner from './pages/AIStudyPlanner';
import AIMockTestGenerator from './pages/AIMockTestGenerator';
import AILearningPath from './pages/AILearningPath';
import AIConceptExplainer from './pages/AIConceptExplainer';
import AIVideoSummarizer from './pages/AIVideoSummarizer';



function AppContent() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth' // Smooth scroll animation
    });
    
  }, [location.pathname]);

  
  return (
    <>
      <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/courses" element={<AllCourses />} />
          <Route path="/course-details/:courseId" element={<CourseDetailsPage />} />
          <Route path="/payment-callback" element={<PaymentCallback />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
         
          
          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          
         
          
          <Route
            path="/my-courses"
            element={
              <ProtectedRoute>
                <MyCourses />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/course/:courseId"
            element={
              <ProtectedRoute>
                <CourseContents />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/course/:courseId/folder/:folderId"
            element={
              <ProtectedRoute>
                <CourseContents />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/content-viewer"
            element={
              <ProtectedRoute>
                <ContentViewer />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/test-series/:examId"
            element={
              <ProtectedRoute>
                <TestSeriesDetail />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/quiz-selection"
            element={
              <ProtectedRoute>
                <QuizSelection />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/quiz"
            element={
              <ProtectedRoute>
                <QuizPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/ai-quiz"
            element={
              <ProtectedRoute>
                <AIQuizPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/results"
            element={
              <ProtectedRoute>
                <ResultPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/print-result"
            element={
              <ProtectedRoute>
                <PrintResultPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <QuizHistory />
              </ProtectedRoute>
            }
          />

          {/* AI Subscription Routes */}
          <Route
            path="/ai/features"
            element={
              <ProtectedRoute>
                <AIFeaturesHub />
              </ProtectedRoute>
            }
          />

          <Route
            path="/subscription/plans"
            element={<SubscriptionPlans />}
          />

          <Route
            path="/subscription/manage"
            element={
              <ProtectedRoute>
                <MySubscription />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ai/quiz-generator"
            element={
              <ProtectedRoute>
                <AIQuizGenerator />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ai/doubt-solver"
            element={
              <ProtectedRoute>
                <AIDoubtSolver />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ai/summarizer"
            element={
              <ProtectedRoute>
                <AINoteSummarizer />
              </ProtectedRoute>
            }
          />

          {/* AI Features */}
          <Route
            path="/ai/flashcards"
            element={
              <ProtectedRoute>
                <AIFlashcardGenerator />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ai/study-planner"
            element={
              <ProtectedRoute>
                <AIStudyPlanner />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ai/mock-test"
            element={
              <ProtectedRoute>
                <AIMockTestGenerator />
              </ProtectedRoute>
            }
          />

          {/* Coming Soon - AI Features */}
          <Route
            path="/ai/learning-path"
            element={
              <ProtectedRoute>
                <AILearningPath />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ai/video-summarizer"
            element={
              <ProtectedRoute>
                <AIVideoSummarizer />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ai/concept-explainer"
            element={
              <ProtectedRoute>
                <AIConceptExplainer />
              </ProtectedRoute>
            }
          />
          
          {/* 404 Route - Redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
          <Route path="/:slug" element={<PolicyPages />} />
        </Routes>

        {/* Mobile Bottom Navigation - Hidden on quiz and print pages */}
        {location.pathname !== '/quiz' && location.pathname !== '/print-result' && <MobileBottomNav />}
      </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <SubscriptionProvider>
            <AppContent />
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;

